/**
 * featureFlags — Read boolean feature flags from Firestore.
 *
 * Flags live in `global_settings/featureFlags` (publicly readable, no auth
 * needed). Missing doc, missing field, or a read error all resolve to false —
 * a flag can gate a flow but never break one. The doc is read once per
 * session (module-level promise cache).
 *
 * PPL-132.
 */

import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';

let flagsPromise: Promise<Record<string, unknown>> | null = null;
let cachedFlags: Record<string, unknown> | null = null;

function loadFlags(): Promise<Record<string, unknown>> {
  return getDoc(doc(FIRESTORE_DB, 'global_settings', 'featureFlags'))
    .then((snap) => (snap.exists() ? snap.data() : {}))
    .catch((err) => {
      console.warn('featureFlags: read failed, defaulting all flags off', err);
      return {};
    })
    .then((flags) => {
      cachedFlags = flags;
      return flags;
    });
}

function flags(): Promise<Record<string, unknown>> {
  if (!flagsPromise) flagsPromise = loadFlags();
  return flagsPromise;
}

/** Get a boolean flag. Anything other than a literal `true` is false. */
export async function getFeatureFlag(name: string): Promise<boolean> {
  return (await flags())[name] === true;
}

/**
 * A flag that is also allow-listable per user: `<name>` is the boolean
 * switch, `<name>Uids` a string[] of uids the flag is on for regardless.
 * Same one Firestore read as `getFeatureFlag`; a read failure is "off".
 */
function flagOrAllowListed(all: Record<string, unknown>, name: string, uid: string | null | undefined): boolean {
  if (all[name] === true) return true;
  if (!uid) return false;
  const list = all[`${name}Uids`];
  return Array.isArray(list) && list.includes(uid);
}

/**
 * The last resolved answer per flag + uid, kept in localStorage so a full
 * page load can seed its first render before the Firestore read settles
 * (the module cache above is empty on every reload). A hint is only ever a
 * previous answer: the live read replaces it and rewrites the hint, so a
 * flag turned off since the last visit corrects itself on the next read.
 * Absent / unreadable → null, which callers treat as "unresolved".
 */
const HINT_PREFIX = 'pplkpr_ff_v1:';

function hintKey(name: string, uid: string | null | undefined): string {
  return `${HINT_PREFIX}${name}:${uid || 'anon'}`;
}

function readHint(name: string, uid: string | null | undefined): boolean | null {
  try {
    const v = localStorage.getItem(hintKey(name, uid));
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeHint(name: string, uid: string | null | undefined, on: boolean): void {
  try {
    localStorage.setItem(hintKey(name, uid), on ? '1' : '0');
  } catch {
    /* private mode / quota — the hint is a convenience */
  }
}

export async function getFeatureFlagForUser(name: string, uid: string | null | undefined): Promise<boolean> {
  const on = flagOrAllowListed(await flags(), name, uid);
  writeHint(name, uid, on);
  return on;
}

/**
 * Synchronous read of a flag (+ allow-list): the session's Firestore read
 * when it has settled, else the localStorage hint from the last resolved
 * read, else `null` (unresolved). Lets a page seed its first render without
 * a flash on every load after the first.
 */
export function getFeatureFlagForUserSync(name: string, uid: string | null | undefined): boolean | null {
  return cachedFlags ? flagOrAllowListed(cachedFlags, name, uid) : readHint(name, uid);
}

/** How long a flag read may run before `withTimeout` falls back. */
export const FLAG_READ_TIMEOUT_MS = 3000;

/**
 * Race `promise` against a `ms` timeout that resolves to `fallback`. The
 * timer is cleared as soon as the race settles, whichever side wins.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), ms);
    }),
  ]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/** PPL-132 — gates the "break the ice" entry fork (invite vs meet a match). */
export function isMeetAMatchEnabled(): Promise<boolean> {
  return getFeatureFlag('meetAMatch');
}

/**
 * PPL-416 — the /f8chat desk. `homeDesk: true` turns it on for everyone,
 * `homeDeskUids: [uid, …]` for an allow-list; anything else (flag off, read
 * failure) leaves the current home untouched.
 */
export function isHomeDeskEnabled(uid: string | null | undefined): Promise<boolean> {
  return getFeatureFlagForUser('homeDesk', uid);
}

export function isHomeDeskEnabledSync(uid: string | null | undefined): boolean | null {
  return getFeatureFlagForUserSync('homeDesk', uid);
}
